/**
 * BulkUpload page (Phase 19.25)
 * Single page with 4 tabs: Quotes · Invoices · Clients · Employees
 * - Drag-and-drop or click to multi-select up to 20 files.
 * - Server runs Gemini Vision (or parses CSV for clients/employees), flags duplicates.
 * - Review table with per-row actions: Skip / Create / Replace / Merge.
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import {
  Upload, FileText, Users, ReceiptText, FilePlus2, X, CheckCircle, AlertTriangle,
  XCircle, GitMerge, Loader2, Sparkles, Mail,
} from 'lucide-react';
import BulkPortalInviteModal from '../components/BulkPortalInviteModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_FILES = 20;

const TABS = [
  { id: 'quote', label: 'Quotes', icon: FileText, types: 'PDF/PNG/JPG/WEBP', accept: '.pdf,.png,.jpg,.jpeg,.webp' },
  { id: 'invoice', label: 'Invoices', icon: ReceiptText, types: 'PDF/PNG/JPG/WEBP', accept: '.pdf,.png,.jpg,.jpeg,.webp' },
  { id: 'client', label: 'Clients', icon: Users, types: 'PDF/Images or CSV', accept: '.pdf,.png,.jpg,.jpeg,.webp,.csv' },
  { id: 'employee', label: 'Employees', icon: FilePlus2, types: 'PDF/Images or CSV', accept: '.pdf,.png,.jpg,.jpeg,.webp,.csv' },
];

export default function BulkUpload() {
  const [searchParams] = useSearchParams();
  const initialKind = ['quote','invoice','client','employee'].includes(searchParams.get('kind'))
    ? searchParams.get('kind') : 'quote';
  const [activeKind, setActiveKind] = useState(initialKind);
  useEffect(() => {
    const k = searchParams.get('kind');
    if (k && ['quote','invoice','client','employee'].includes(k) && k !== activeKind) {
      setActiveKind(k);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  return (
    <div className="p-6 space-y-6">
      <header>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-cyan-500" />
          Bulk Upload &amp; AI Extraction
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Drag up to <strong>{MAX_FILES}</strong> files at once. AI reads them and creates draft records. Duplicates are flagged so you can skip, replace, or merge.
        </p>
      </header>

      <Tabs value={activeKind} onValueChange={setActiveKind} data-testid="bulk-upload-tabs">
        <TabsList className="grid grid-cols-2 sm:grid-cols-4 w-full h-auto gap-1 p-1">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <TabsTrigger key={t.id} value={t.id} className="flex items-center gap-2 py-2" data-testid={`bulk-tab-${t.id}`}>
                <Icon className="h-4 w-4" /> {t.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {TABS.map(t => (
          <TabsContent key={t.id} value={t.id} className="space-y-4">
            <BulkPanel kind={t.id} label={t.label} accept={t.accept} types={t.types} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

const BulkPanel = ({ kind, label, accept, types }) => {
  const { token } = useAuth();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [batch, setBatch] = useState(null);
  const inputRef = useRef(null);

  const onPickFiles = (list) => {
    const arr = Array.from(list || []);
    if (!arr.length) return;
    const next = [...files, ...arr].slice(0, MAX_FILES);
    if (arr.length + files.length > MAX_FILES) {
      toast.warning(`Capped at ${MAX_FILES} files per batch`);
    }
    setFiles(next);
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    onPickFiles(e.dataTransfer?.files);
  }, [files]);

  const removeAt = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const handleUpload = async () => {
    if (!files.length) return;
    setUploading(true);
    setBatch(null);
    try {
      const fd = new FormData();
      fd.append('kind', kind);
      files.forEach(f => fd.append('files', f));
      const resp = await axios.post(`${API_URL}/api/bulk-ingest/upload`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        timeout: 5 * 60 * 1000,
      });
      setBatch(resp.data);
      setFiles([]);
      toast.success(`Processed ${resp.data.total} item(s)`);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Bulk {label} Upload
        </CardTitle>
        <CardDescription>
          Up to <strong>{MAX_FILES}</strong> files at once · {types} · 15 MB max per file
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Drop zone */}
        <div
          onDrop={onDrop}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onClick={() => inputRef.current?.click()}
          className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
          data-testid={`bulk-dropzone-${kind}`}
        >
          <Upload className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="mt-2 text-sm">
            <span className="font-medium text-primary">Click to choose</span> or drag &amp; drop here
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {types} · up to {MAX_FILES} files
          </p>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={accept}
            onChange={(e) => onPickFiles(e.target.files)}
            className="hidden"
            data-testid={`bulk-file-input-${kind}`}
          />
        </div>

        {/* Selected files */}
        {files.length > 0 && (
          <div className="space-y-2 max-h-60 overflow-auto rounded border p-2 bg-muted/30" data-testid={`bulk-file-list-${kind}`}>
            {files.map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="flex-1 truncate">{f.name}</span>
                <span className="text-xs text-muted-foreground">{(f.size / 1024).toFixed(0)} KB</span>
                <button onClick={() => removeAt(i)} className="text-muted-foreground hover:text-destructive">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end">
          <Button
            onClick={handleUpload}
            disabled={!files.length || uploading}
            data-testid={`bulk-upload-btn-${kind}`}
          >
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            {uploading ? 'Processing…' : `Extract & Process ${files.length || 0} file${files.length === 1 ? '' : 's'}`}
          </Button>
        </div>

        {/* Results review */}
        {batch && <BatchReview batch={batch} setBatch={setBatch} kind={kind} />}
      </CardContent>
    </Card>
  );
};

const STATUS_LOOK = {
  ready:     { color: 'bg-blue-500/10 text-blue-700',     label: 'Ready',     Icon: CheckCircle },
  duplicate: { color: 'bg-amber-500/10 text-amber-700',   label: 'Duplicate', Icon: AlertTriangle },
  created:   { color: 'bg-green-500/10 text-green-700',   label: 'Created',   Icon: CheckCircle },
  skipped:   { color: 'bg-muted text-muted-foreground',   label: 'Skipped',   Icon: XCircle },
  merged:    { color: 'bg-violet-500/10 text-violet-700', label: 'Merged',    Icon: GitMerge },
  replaced:  { color: 'bg-violet-500/10 text-violet-700', label: 'Replaced',  Icon: GitMerge },
  failed:    { color: 'bg-rose-500/10 text-rose-700',     label: 'Failed',    Icon: XCircle },
};

const BatchReview = ({ batch, setBatch, kind }) => {
  const { token } = useAuth();
  const [busy, setBusy] = useState({});
  const [mergeItem, setMergeItem] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [autoInvitePref, setAutoInvitePref] = useState(false);
  const [autoTriggered, setAutoTriggered] = useState(false);

  // Read tenant pref so auto-invite triggers after acceptAllReady when ON.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/subscriptions/me/tenant-prefs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setAutoInvitePref(!!res.data?.auto_send_portal_invite_on_bulk_import);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const refreshBatch = async () => {
    const resp = await axios.get(`${API_URL}/api/bulk-ingest/batches/${batch.batch_id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setBatch({ ...batch, items: resp.data.items });
  };

  const resolve = async (item, action, mergeFields) => {
    setBusy({ ...busy, [item.id]: action });
    try {
      await axios.post(
        `${API_URL}/api/bulk-ingest/items/${item.id}/resolve`,
        { action, merge_fields: mergeFields || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshBatch();
      toast.success(`Item ${action}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setBusy({ ...busy, [item.id]: null });
    }
  };

  const acceptAllReady = async () => {
    const ready = batch.items.filter(i => i.status === 'ready');
    for (const it of ready) {
      // eslint-disable-next-line no-await-in-loop
      await resolve(it, 'create');
    }
    // Phase 19.28 — auto-open invite modal when tenant pref is ON for client/employee imports.
    if (autoInvitePref && (kind === 'client' || kind === 'employee') && !autoTriggered) {
      setAutoTriggered(true);
      setTimeout(() => setInviteOpen(true), 600);
    }
  };

  const dupesCount = batch.items.filter(i => i.status === 'duplicate').length;
  const readyCount = batch.items.filter(i => i.status === 'ready').length;
  const createdCount = batch.items.filter(i => ['created', 'replaced', 'merged'].includes(i.status)).length;
  const canInvitePortal = (kind === 'client' || kind === 'employee') && createdCount > 0;

  return (
    <div className="border rounded-lg p-4 space-y-3 bg-muted/20" data-testid="bulk-batch-review">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm">
          <strong>{batch.total}</strong> item(s) processed ·{' '}
          <span className="text-blue-700">{readyCount} ready</span> ·{' '}
          <span className="text-amber-700">{dupesCount} duplicate</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {readyCount > 0 && (
            <Button size="sm" onClick={acceptAllReady} data-testid="bulk-accept-all-btn">
              Create all {readyCount} ready items
            </Button>
          )}
          {canInvitePortal && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setInviteOpen(true)}
              data-testid="bulk-send-portal-invites-btn"
            >
              <Mail className="h-4 w-4 mr-2" />
              Send portal invites to new records
            </Button>
          )}
        </div>
      </div>
      <div className="space-y-2">
        {batch.items.map((it) => (
          <ReviewRow
            key={it.id}
            item={it}
            kind={kind}
            busy={busy[it.id]}
            onResolve={resolve}
            onOpenMerge={() => setMergeItem(it)}
          />
        ))}
      </div>
      {mergeItem && (
        <MergeDialog
          item={mergeItem}
          onClose={() => setMergeItem(null)}
          onApply={async (fields) => {
            await resolve(mergeItem, 'merge', fields);
            setMergeItem(null);
          }}
        />
      )}
      {/* Phase 19.23 — bulk portal invite for this batch's created records */}
      <BulkPortalInviteModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        accountType={kind === 'client' ? 'client_portal' : 'employee_portal'}
        batchId={batch.batch_id}
        onSent={() => setInviteOpen(false)}
      />
    </div>
  );
};

const ReviewRow = ({ item, kind, busy, onResolve, onOpenMerge }) => {
  const look = STATUS_LOOK[item.status] || STATUS_LOOK.ready;
  const Icon = look.Icon;
  const title = item.extracted?.name || item.extracted?.email
    || item.extracted?.quote_number || item.extracted?.invoice_number
    || item.source_filename || 'Untitled';
  const sub = item.extracted?.email || item.extracted?.client_name || item.source_filename;
  const terminal = !['ready', 'duplicate'].includes(item.status);

  return (
    <div className="bg-background rounded border p-3 flex items-center gap-3 flex-wrap" data-testid={`bulk-row-${item.id}`}>
      <Badge className={look.color}>
        <Icon className="h-3 w-3 mr-1" /> {look.label}
      </Badge>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{title}</p>
        <p className="text-xs text-muted-foreground truncate">{sub}</p>
        {item.duplicate_of && (
          <p className="text-xs text-amber-700 mt-0.5">
            ⚠ Matches existing: <strong>{item.duplicate_of.label}</strong>
          </p>
        )}
        {item.error && <p className="text-xs text-rose-700 mt-0.5">{item.error}</p>}
      </div>
      {!terminal && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="ghost" onClick={() => onResolve(item, 'skip')} disabled={!!busy} data-testid={`row-skip-${item.id}`}>
            Skip
          </Button>
          {item.status === 'ready' && (
            <Button size="sm" onClick={() => onResolve(item, 'create')} disabled={!!busy} data-testid={`row-create-${item.id}`}>
              {busy === 'create' ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Create'}
            </Button>
          )}
          {item.status === 'duplicate' && (
            <>
              <Button size="sm" variant="outline" onClick={() => onResolve(item, 'replace')} disabled={!!busy} data-testid={`row-replace-${item.id}`}>
                Replace
              </Button>
              <Button size="sm" onClick={onOpenMerge} disabled={!!busy} data-testid={`row-merge-${item.id}`}>
                <GitMerge className="h-3 w-3 mr-1" /> Merge…
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const MergeDialog = ({ item, onClose, onApply }) => {
  const existing = item.duplicate_of?.fields || {};
  const incoming = item.extracted || {};
  // Build the unique union of keys to display
  const keys = Array.from(new Set([
    ...Object.keys(existing), ...Object.keys(incoming),
  ])).filter(k => k !== 'items');
  // Per-key choice: 'existing' | 'new'
  const initialChoice = {};
  keys.forEach(k => {
    initialChoice[k] = (incoming[k] && !existing[k]) ? 'new' : 'existing';
  });
  const [choices, setChoices] = useState(initialChoice);

  const apply = () => {
    const merge_fields = {};
    keys.forEach(k => {
      if (choices[k] === 'new' && incoming[k] != null) {
        merge_fields[k] = incoming[k];
      }
    });
    onApply(merge_fields);
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="bulk-merge-dialog">
        <DialogHeader>
          <DialogTitle>Merge into existing record</DialogTitle>
          <DialogDescription>
            For each field, pick the value to keep. New values are only written when explicitly selected.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[55vh] overflow-auto space-y-1 text-sm">
          <div className="grid grid-cols-12 gap-2 font-medium text-xs text-muted-foreground border-b pb-2 sticky top-0 bg-background">
            <div className="col-span-3">Field</div>
            <div className="col-span-4">Existing</div>
            <div className="col-span-4">From upload</div>
            <div className="col-span-1 text-right">Keep</div>
          </div>
          {keys.map(k => {
            const ev = existing[k];
            const nv = incoming[k];
            const choice = choices[k] || 'existing';
            const fmt = (v) => {
              if (v == null || v === '') return <span className="text-muted-foreground italic">empty</span>;
              if (typeof v === 'object') return <code className="text-xs">{JSON.stringify(v)}</code>;
              return String(v);
            };
            return (
              <div key={k} className="grid grid-cols-12 gap-2 items-center py-1.5 border-b last:border-0">
                <div className="col-span-3 font-mono text-xs">{k}</div>
                <div className="col-span-4 break-all">{fmt(ev)}</div>
                <div className="col-span-4 break-all">{fmt(nv)}</div>
                <div className="col-span-1 flex justify-end gap-1">
                  <button
                    type="button"
                    onClick={() => setChoices({ ...choices, [k]: 'existing' })}
                    className={`px-1.5 py-0.5 rounded text-[10px] ${choice === 'existing' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
                    data-testid={`merge-keep-existing-${k}`}
                  >
                    old
                  </button>
                  <button
                    type="button"
                    onClick={() => setChoices({ ...choices, [k]: 'new' })}
                    className={`px-1.5 py-0.5 rounded text-[10px] ${choice === 'new' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}
                    disabled={nv == null || nv === ''}
                    data-testid={`merge-keep-new-${k}`}
                  >
                    new
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={apply} data-testid="bulk-merge-apply-btn">Apply merge</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
